import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, Plus, X, CheckCircle2, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  who:"", what:"", where:"", when: new Date().toISOString().slice(0,16),
  severity:"low", description:"", witnesses:"", corrective_action:"", attachments: [],
  individual_id: "",
};

async function fileToDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ name: file.name, type: file.type, data_url: r.result });
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Incidents() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [f, setF] = useState(BLANK);

  const load = async () => {
    const r = await api.get("/incidents");
    setRows(r.data);
    // Sprint 3.5 (M3.5): load participants for optional attachment.
    // Incidents are OFTEN participant-related but not always (staff-only,
    // environmental). Picker stays optional to reflect that.
    if (user.role === "admin" || user.role === "rn" || user.role === "dsp") {
      try {
        const inds = await api.get("/individuals", { params: { limit: 500 } });
        setIndividuals((inds.data.data || []).filter(
          (i) => i.status === "active" || i.status === "intake",
        ));
      } catch { /* DSPs may have restricted access */ }
    }
  };
  useEffect(() => { load(); }, []);

  const criticalOpen = rows.filter(i => (i.severity==="critical"||i.severity==="high") && i.status==="submitted");

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const acceptable = files.filter(f => f.size <= 5*1024*1024);
    if (acceptable.length < files.length) toast.error("Some files > 5MB were skipped");
    try {
      const parts = await Promise.all(acceptable.map(fileToDataUrl));
      setF(p => ({...p, attachments: [...p.attachments, ...parts].slice(0,6)}));
    } catch { toast.error("Failed to read file"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/incidents", {
        ...f,
        when: new Date(f.when).toISOString(),
        // Send null (not empty string) so backend Optional validation is clean
        individual_id: f.individual_id || null,
      });
      toast.success("Incident submitted");
      setOpen(false); setF(BLANK); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (id) => {
    try { await api.patch(`/incidents/${id}/review`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8">
      {criticalOpen.length > 0 && (
        <div className="rounded-2xl px-5 py-4 compliance-expired flex items-center gap-3" data-testid="critical-incident-banner">
          <AlertTriangle size={20}/>
          <div>
            <div className="font-semibold" style={{fontFamily:'Outfit'}}>
              {criticalOpen.length} open CRITICAL/HIGH incident{criticalOpen.length>1?"s":""}
            </div>
            <div className="text-xs opacity-90">
              Codes: {criticalOpen.map(i=>i.code).join(", ")} — Admin email sent (MOCKED in logs).
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="accp-label">PORII</div>
          <h1 className="text-4xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Incident Reports</h1>
          <p className="text-muted-foreground mt-1">Who / What / Where / When · Corrective Action · attachments.</p>
        </div>
        <button className="accp-btn-primary" onClick={()=>setOpen(true)} data-testid="add-incident-btn">
          <Plus size={16}/> New report
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((i) => (
          <div key={i.id}
               className={`accp-card p-5 cursor-pointer ${(i.severity==="critical"||i.severity==="high") && i.status==="submitted" ? "ring-1 ring-[#B71C1C]/60" : ""}`}
               onClick={()=>setViewing(i)} data-testid={`incident-${i.code}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="accp-label font-mono">{i.code} · severity {i.severity}</div>
                <h3 className="font-semibold text-primary mt-1" style={{fontFamily:'Outfit'}}>{i.what}</h3>
              </div>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                i.status==="reviewed" ? "compliance-valid" :
                i.severity==="critical" || i.severity==="high" ? "compliance-expired" : "compliance-pending"
              }`}>{i.status}</span>
            </div>
            <div className="mt-3 text-sm text-muted-foreground space-y-1">
              <div><span className="text-primary font-semibold">Who:</span> {i.who}</div>
              <div><span className="text-primary font-semibold">Where:</span> {i.where}</div>
              <div><span className="text-primary font-semibold">When:</span> {new Date(i.when).toLocaleString()}</div>
              <div><span className="text-primary font-semibold">Reporter:</span> {i.reporter_name} ({i.reporter_code})</div>
              {i.attachments?.length > 0 && <div className="text-xs"><Paperclip size={12} className="inline mr-1"/>{i.attachments.length} attachment(s)</div>}
            </div>
            {(user.role==="admin"||user.role==="rn") && i.status==="submitted" && (
              <button className="accp-btn-ghost text-xs mt-3" onClick={(e)=>{e.stopPropagation(); review(i.id);}}>
                <CheckCircle2 size={13}/> Mark reviewed
              </button>
            )}
          </div>
        ))}
        {rows.length===0 && <div className="text-muted-foreground col-span-full">No reports.</div>}
      </div>

      {open && (
        <div className="accp-modal-shell" onClick={()=>setOpen(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="accp-modal-card accp-modal-card-lg space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-primary flex items-center gap-2" style={{fontFamily:'Outfit'}}>
                <AlertTriangle size={18}/> Report incident
              </h3>
              <button type="button" onClick={()=>setOpen(false)}><X size={18}/></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2"><span className="accp-label">Who (client/resident)</span>
                <input required className="accp-input mt-1" value={f.who} onChange={(e)=>setF({...f, who:e.target.value})}/>
              </label>
              <label className="col-span-2"><span className="accp-label">Participant (optional)</span>
                <select className="accp-input mt-1"
                  value={f.individual_id}
                  onChange={(e) => setF({ ...f, individual_id: e.target.value })}
                  data-testid="incident-individual-picker">
                  <option value="">— Not linked (staff-only or environmental) —</option>
                  {individuals.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.code} — {i.last_name}, {i.first_name}
                    </option>
                  ))}
                </select>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Link this incident to a participant when the participant is the
                  subject. Leave unlinked for staff-only or environmental incidents.
                </div>
              </label>
              <label className="col-span-2"><span className="accp-label">What happened</span>
                <input required className="accp-input mt-1" value={f.what} onChange={(e)=>setF({...f, what:e.target.value})}/>
              </label>
              <label><span className="accp-label">Where</span>
                <input required className="accp-input mt-1" value={f.where} onChange={(e)=>setF({...f, where:e.target.value})}/>
              </label>
              <label><span className="accp-label">When</span>
                <input type="datetime-local" required className="accp-input mt-1" value={f.when} onChange={(e)=>setF({...f, when:e.target.value})}/>
              </label>
              <label className="col-span-2"><span className="accp-label">Severity</span>
                <select className="accp-input mt-1" value={f.severity} onChange={(e)=>setF({...f, severity:e.target.value})}>
                  <option value="low">Low</option><option value="medium">Medium</option>
                  <option value="high">High</option><option value="critical">Critical</option>
                </select>
              </label>
              <label className="col-span-2"><span className="accp-label">Description</span>
                <textarea required rows={3} className="accp-input mt-1" value={f.description} onChange={(e)=>setF({...f, description:e.target.value})}/>
              </label>
              <label className="col-span-2"><span className="accp-label">Witnesses</span>
                <input className="accp-input mt-1" value={f.witnesses} onChange={(e)=>setF({...f, witnesses:e.target.value})}/>
              </label>
              <label className="col-span-2"><span className="accp-label">Corrective Action (COA)</span>
                <textarea rows={3} className="accp-input mt-1" placeholder="Steps taken / to be taken to prevent recurrence"
                  value={f.corrective_action} onChange={(e)=>setF({...f, corrective_action:e.target.value})}/>
              </label>
              <div className="col-span-2">
                <span className="accp-label">Attachments (photos, PDFs — max 6 files, 5MB each)</span>
                <input type="file" multiple accept="image/*,.pdf" onChange={onFiles} className="block mt-2 text-sm" data-testid="incident-attachments"/>
                {f.attachments.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {f.attachments.map((a, i) => (
                      <li key={i} className="text-xs flex items-center justify-between bg-accent/40 rounded-lg px-2 py-1">
                        <span className="truncate"><Paperclip size={11} className="inline mr-1"/>{a.name}</span>
                        <button type="button" onClick={()=>setF({...f, attachments: f.attachments.filter((_,x)=>x!==i)})}>
                          <Trash2 size={12}/>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <button type="submit" className="accp-btn-primary w-full" data-testid="incident-submit">Submit report</button>
          </form>
        </div>
      )}

      {viewing && (
        <div className="accp-modal-shell" onClick={()=>setViewing(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="accp-modal-card accp-modal-card-xl space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="accp-label font-mono">{viewing.code} · {viewing.severity}</div>
                <h3 className="text-2xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>{viewing.what}</h3>
              </div>
              <button onClick={()=>setViewing(null)}><X size={18}/></button>
            </div>
            <div className="text-sm space-y-1 text-muted-foreground">
              <div><b className="text-primary">Who:</b> {viewing.who}</div>
              <div><b className="text-primary">Where:</b> {viewing.where}</div>
              <div><b className="text-primary">When:</b> {new Date(viewing.when).toLocaleString()}</div>
              <div><b className="text-primary">Reporter:</b> {viewing.reporter_name} ({viewing.reporter_code})</div>
            </div>
            <div>
              <div className="accp-label">Description</div>
              <p className="text-sm text-primary/90 mt-1 whitespace-pre-wrap">{viewing.description}</p>
            </div>
            {viewing.corrective_action && (
              <div>
                <div className="accp-label">Corrective Action (COA)</div>
                <p className="text-sm text-primary/90 mt-1 whitespace-pre-wrap">{viewing.corrective_action}</p>
              </div>
            )}
            {viewing.attachments?.length > 0 && (
              <div>
                <div className="accp-label">Attachments</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {viewing.attachments.map((a, i) => (
                    <a key={i} href={a.data_url} download={a.name} className="block rounded-lg border border-border overflow-hidden hover:border-primary/40">
                      {a.type.startsWith("image/")
                        ? <img src={a.data_url} alt={a.name} className="w-full h-24 object-cover"/>
                        : <div className="p-3 text-xs text-muted-foreground text-center h-24 flex items-center justify-center"><Paperclip size={14} className="mr-1"/>{a.name}</div>}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
